import { Fragment, forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search, X } from 'lucide-react'

/**
 * In-app dropdowns — the kit never shows the browser's native <select> menu.
 *
 *   <Select>        short lists (≈ ≤ 12 options): branch, status, type.
 *                   Type a letter to jump. No search box.
 *   <SearchSelect>  long lists: doctors, employees, billing types, sellers.
 *                   Search box on top; `multiple` for multi-pick.
 *
 * Behaviour is components/SearchableSelect.jsx's (the app's existing picker —
 * same option shape, same keys), plus what the audit found missing:
 *   - the menu is PORTALLED and fixed-positioned, so it is never clipped by a
 *     Drawer body or a scrolling table, and flips upward near the bottom edge
 *   - Esc closes the MENU only (it does not reach the Drawer underneath)
 *   - values compare as strings: 8 and '8' are the same branch
 *   - invalid styling + label wiring when used inside <Field>
 *   - multi shows the picked names ("อารีย์, บางนา" / "อารีย์ +2"), not just a count
 *
 * options: [{ value, label, hint?, count?, search?, group?, disabled?, selectedLabel? }]
 *   hint           second grey line (what tells two "หมอเอ" apart)
 *   search         extra text the search matches but never shows
 *   group          sticky header each time it changes (sort by it yourself)
 *   selectedLabel  what the closed trigger shows instead of `label`
 *
 * Single: value / onChange(value). Clearing gives ''.
 * Multiple: values (Set or array) / onChange(Set).
 */

const same = (a, b) => String(a ?? '') === String(b ?? '')
const MENU_MAX_H = 280

const TRIGGER =
  'w-full flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-[13.5px] text-left transition ' +
  'focus:outline-none disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed'

function usePicker({ options, value, values, multiple, onChange, searchable }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)
  const sel = useMemo(() => (multiple ? new Set([...(values instanceof Set ? values : values || [])].map(String)) : null), [multiple, values])

  const filtered = useMemo(() => {
    const needle = searchable ? q.trim().toLowerCase() : ''
    if (!needle) return options
    return options.filter((o) => [o.label, o.value, o.hint, o.group, o.search]
      .some((f) => String(f ?? '').toLowerCase().includes(needle)))
  }, [options, q, searchable])

  // Opening highlights the current value (single) so ↑/↓ start from it.
  useEffect(() => {
    if (!open) { setQ(''); return }
    const at = multiple ? -1 : filtered.findIndex((o) => same(o.value, value))
    setHi(at >= 0 ? at : Math.max(0, filtered.findIndex((o) => !o.disabled)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => { if (open && q) setHi(Math.max(0, filtered.findIndex((o) => !o.disabled))) }, [q]) // eslint-disable-line react-hooks/exhaustive-deps

  const isOn = (o) => (multiple ? sel.has(String(o.value)) : same(o.value, value))

  const commit = (o) => {
    if (!o || o.disabled) return
    if (multiple) {
      const next = new Set(sel)
      const k = String(o.value)
      if (next.has(k)) next.delete(k); else next.add(k)
      // Hand back the caller's own value types, not stringified ones.
      onChange?.(new Set(options.filter((x) => next.has(String(x.value))).map((x) => x.value)))
      setQ('')
    } else {
      onChange?.(o.value)
      setOpen(false)
    }
  }

  const move = (dir) => {
    const n = filtered.length
    if (!n) return
    let i = hi
    for (let step = 0; step < n; step++) {
      i = (i + dir + n) % n
      if (!filtered[i].disabled) break
    }
    setHi(i)
  }

  return { open, setOpen, q, setQ, hi, setHi, sel, filtered, isOn, commit, move }
}

/** Fixed-position menu under (or above) the trigger, kept in place on scroll / resize. */
function useMenuPosition(open, triggerRef, minWidth) {
  const [pos, setPos] = useState(null)
  const place = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const below = window.innerHeight - r.bottom
    const up = below < MENU_MAX_H + 12 && r.top > below
    const width = Math.max(r.width, minWidth)
    const left = Math.min(r.left, window.innerWidth - width - 8)
    setPos(up
      ? { left, width, bottom: window.innerHeight - r.top + 4, maxH: Math.min(MENU_MAX_H, r.top - 12) }
      : { left, width, top: r.bottom + 4, maxH: Math.min(MENU_MAX_H, below - 12) })
  }, [triggerRef, minWidth])
  useLayoutEffect(() => {
    if (!open) return undefined
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place) }
  }, [open, place])
  return pos
}

function Menu({ pos, menuRef, listRef, p, searchable, searchPlaceholder, emptyText, multiple, inputRef, onKeyDown }) {
  useEffect(() => {
    if (!p.open || !listRef.current) return
    listRef.current.querySelector(`[data-idx="${p.hi}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [p.hi, p.open, listRef])
  if (!pos) return null
  return createPortal(
    <div ref={menuRef} role="listbox" aria-multiselectable={multiple || undefined}
      style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
      className="z-[60] rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 overflow-hidden animate-ss-pop-in">
      {searchable && (
        <div className="relative border-b border-slate-100">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input ref={inputRef} value={p.q} onChange={(e) => p.setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none" />
        </div>
      )}
      <div ref={listRef} className="overflow-y-auto py-1" style={{ maxHeight: Math.max(120, pos.maxH - (searchable ? 40 : 0)) }}>
        {!p.filtered.length && <div className="px-3 py-3 text-[13px] text-slate-400">{emptyText}</div>}
        {p.filtered.map((o, i) => {
          const on = p.isOn(o)
          const head = o.group && o.group !== p.filtered[i - 1]?.group ? o.group : null
          return (
            <Fragment key={String(o.value)}>
              {head && <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm px-3 pt-1.5 pb-1 text-[11.5px] font-semibold text-slate-400 border-b border-slate-100">{head}</div>}
              <div role="option" aria-selected={on} aria-disabled={o.disabled || undefined} data-idx={i}
                onMouseEnter={() => !o.disabled && p.setHi(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => p.commit(o)}
                className={`flex items-center gap-2 px-3 py-1.5 text-[13.5px] scroll-mt-8 ${
                  o.disabled ? 'text-slate-300 cursor-not-allowed' : 'cursor-pointer'} ${
                  i === p.hi && !o.disabled ? 'bg-brand-50' : ''} ${on ? 'text-brand-700 font-medium' : o.disabled ? '' : 'text-slate-700'}`}>
                <span className={`w-4 shrink-0 ${on ? 'text-brand-600' : 'text-transparent'}`}><Check size={14} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{o.label}</span>
                  {o.hint && <span className="block truncate text-[12px] font-normal text-slate-400">{o.hint}</span>}
                </span>
                {o.count != null && <span className="text-[12px] text-slate-400 tabular-nums shrink-0">{o.count}</span>}
              </div>
            </Fragment>
          )
        })}
      </div>
    </div>,
    document.body,
  )
}

function triggerLabel({ options, value, multiple, sel, placeholder }) {
  if (multiple) {
    const picked = options.filter((o) => sel.has(String(o.value)))
    if (!picked.length) return { text: placeholder, empty: true }
    const names = picked.map((o) => o.selectedLabel || o.label)
    return { text: names.length <= 2 ? names.join(', ') : `${names[0]} +${names.length - 1}`, title: names.join(', '), empty: false }
  }
  const o = options.find((x) => same(x.value, value))
  if (!o || value === '' || value == null) return { text: placeholder, empty: true }
  const t = o.selectedLabel || o.label
  return { text: t, title: t, empty: false }
}

const Picker = forwardRef(function Picker({
  options = [], value = '', values, multiple = false, onChange, searchable = false, clearable = false,
  placeholder = 'เลือก…', searchPlaceholder = 'พิมพ์เพื่อค้นหา…', emptyText = 'ไม่พบตัวเลือก',
  disabled = false, invalid = false, id, minMenuWidth = 220, className = '',
}, fwdRef) {
  const p = usePicker({ options, value, values, multiple, onChange, searchable })
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const listRef = useRef(null)
  const inputRef = useRef(null)
  const typed = useRef({ buf: '', t: 0 })
  const pos = useMenuPosition(p.open, triggerRef, minMenuWidth)

  const setTriggerRef = (el) => {
    triggerRef.current = el
    if (typeof fwdRef === 'function') fwdRef(el); else if (fwdRef) fwdRef.current = el
  }

  // Outside click closes (the menu lives in a portal, so check both nodes).
  useEffect(() => {
    if (!p.open) return undefined
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return
      p.setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [p.open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (p.open && searchable) inputRef.current?.focus() }, [p.open, searchable])

  const close = (refocus = true) => { p.setOpen(false); if (refocus) triggerRef.current?.focus() }

  const onKeyDown = (e) => {
    const k = e.key
    if (k === 'Escape') {
      if (!p.open) return                     // let it reach the Drawer / Modal
      e.preventDefault(); e.stopPropagation(); close(); return
    }
    if (k === 'Tab') { if (p.open) close(false); return }
    if (k === 'ArrowDown' || k === 'ArrowUp') {
      e.preventDefault()
      if (!p.open) { p.setOpen(true); return }
      p.move(k === 'ArrowDown' ? 1 : -1); return
    }
    if (k === 'Home' || k === 'End') { if (p.open) { e.preventDefault(); p.setHi(k === 'Home' ? 0 : p.filtered.length - 1) } return }
    if (k === 'Enter' || (k === ' ' && !searchable)) {
      e.preventDefault()
      if (!p.open) { p.setOpen(true); return }
      p.commit(p.filtered[p.hi])
      if (!multiple) triggerRef.current?.focus()
      return
    }
    if (k === 'Backspace' && multiple && searchable && !p.q && p.sel.size) {
      const last = [...p.sel].pop()
      onChange?.(new Set(options.filter((o) => p.sel.has(String(o.value)) && String(o.value) !== last).map((o) => o.value)))
      return
    }
    // Type-to-jump on the short Select (no search box).
    if (!searchable && k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const now = Date.now()
      typed.current = { buf: (now - typed.current.t < 700 ? typed.current.buf : '') + k.toLowerCase(), t: now }
      const at = options.findIndex((o) => !o.disabled && String(o.label).toLowerCase().startsWith(typed.current.buf))
      if (at < 0) return
      if (p.open) p.setHi(at); else if (!multiple) onChange?.(options[at].value)
    }
  }

  const lab = triggerLabel({ options, value, multiple, sel: p.sel, placeholder })
  const showClear = !disabled && !lab.empty && (multiple || clearable)
  const ring = invalid
    ? 'border-rose-300 focus-visible:ring-4 focus-visible:ring-rose-100'
    : p.open ? 'border-brand-400 ring-4 ring-brand-100' : 'border-slate-200 hover:border-slate-300 focus-visible:border-brand-400 focus-visible:ring-4 focus-visible:ring-brand-100'

  return (
    <div className={`relative ${className}`}>
      <button ref={setTriggerRef} id={id} type="button" disabled={disabled}
        role="combobox" aria-haspopup="listbox" aria-expanded={p.open} aria-invalid={invalid || undefined}
        title={lab.empty ? undefined : lab.title}
        onClick={() => p.setOpen((v) => !v)} onKeyDown={onKeyDown}
        className={`${TRIGGER} ${ring} ${lab.empty ? 'text-slate-400' : 'text-slate-800'}`}>
        <span className="flex-1 truncate">{lab.text}</span>
        {showClear && (
          <span role="button" tabIndex={-1} title="ล้าง" aria-label="ล้าง"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => { e.stopPropagation(); onChange?.(multiple ? new Set() : '') }}
            className="text-slate-300 hover:text-slate-600 -my-1 p-0.5 rounded"><X size={14} /></span>
        )}
        <ChevronDown size={15} className={`text-slate-400 shrink-0 transition-transform ${p.open ? 'rotate-180' : ''}`} />
      </button>
      {p.open && (
        <Menu pos={pos} menuRef={menuRef} listRef={listRef} inputRef={inputRef} p={p} searchable={searchable}
          searchPlaceholder={searchPlaceholder} emptyText={emptyText} multiple={multiple} onKeyDown={onKeyDown} />
      )}
    </div>
  )
})

/** Short list, in-app menu, type a letter to jump. */
export const Select = forwardRef(function Select(props, ref) {
  return <Picker ref={ref} {...props} searchable={false} />
})

/** Long list with a search box. `multiple` for multi-pick (values / onChange(Set)). */
export const SearchSelect = forwardRef(function SearchSelect(props, ref) {
  return <Picker ref={ref} {...props} searchable />
})
